import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Package, Loader2 } from "lucide-react";
import { apiClient } from "../lib/api";

const OrderConfirmation = () => {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get(`/orders/${orderNumber}`)
      .then(({ data }) => setOrder(data))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [orderNumber]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#F39C12]" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h1 className="font-display text-3xl font-bold">Order not found</h1>
        <p className="mt-3 text-[#7F8C8D]">
          We couldn't find an order with number {orderNumber}.
        </p>
        <Link
          to="/"
          className="mt-6 inline-block cta-red rounded-full px-6 py-3 font-semibold"
        >
          Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="font-body py-14">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border border-[#F39C12]/20 rounded-3xl p-8 text-center">
          <div
            className="mx-auto w-16 h-16 rounded-full bg-[#27AE60]/15 flex items-center justify-center"
            data-testid="order-success-icon"
          >
            <CheckCircle2 className="w-8 h-8 text-[#27AE60]" />
          </div>
          <h1 className="mt-5 font-display text-3xl sm:text-4xl font-bold text-[#2C3E50]">
            Order placed successfully!
          </h1>
          <p className="mt-3 text-[#2C3E50]/85">
            Thank you, {order.shipping.name}. We'll call you shortly to confirm
            your Cash on Delivery order.
          </p>

          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFF5E1] border border-[#F39C12]/30">
            <Package className="w-4 h-4 text-[#D63031]" />
            <span className="text-sm">
              Order number:{" "}
              <strong
                data-testid="confirmation-order-number"
                className="font-display"
              >
                #{order.order_number}
              </strong>
            </span>
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-5">
          <div className="bg-white border border-[#F39C12]/20 rounded-2xl p-6">
            <p className="text-xs uppercase tracking-widest text-[#F39C12] font-semibold mb-2">
              Shipping to
            </p>
            <p className="font-display font-semibold text-[#2C3E50]">
              {order.shipping.name}
            </p>
            <p className="text-sm text-[#2C3E50]/85 mt-1">
              {order.shipping.address_line_1}
              {order.shipping.address_line_2
                ? `, ${order.shipping.address_line_2}`
                : ""}
              , {order.shipping.city}, {order.shipping.state} -{" "}
              {order.shipping.pincode}
            </p>
            <p className="text-sm text-[#7F8C8D] mt-1">
              Mobile: {order.shipping.mobile}
            </p>
          </div>

          <div className="bg-white border border-[#F39C12]/20 rounded-2xl p-6">
            <p className="text-xs uppercase tracking-widest text-[#F39C12] font-semibold mb-3">
              Items
            </p>
            <ul className="space-y-2 text-sm">
              {order.items.map((it) => (
                <li
                  key={it.product_id}
                  className="flex items-center justify-between"
                >
                  <span>
                    {it.name} × {it.quantity}
                  </span>
                  <span className="font-semibold">
                    ₹{(it.price * it.quantity).toLocaleString("en-IN")}
                  </span>
                </li>
              ))}
            </ul>
            <div className="border-t border-[#F39C12]/15 mt-4 pt-3 flex items-center justify-between">
              <span className="text-sm text-[#7F8C8D]">Total (COD)</span>
              <span className="font-display text-xl font-bold text-[#D63031]">
                ₹{order.total.toLocaleString("en-IN")}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to={`/track-order`}
            data-testid="confirmation-track-btn"
            className="cta-gold rounded-full h-12 px-7 font-semibold inline-flex items-center justify-center"
          >
            Track this order
          </Link>
          <Link
            to="/"
            className="rounded-full h-12 px-7 font-semibold inline-flex items-center justify-center bg-white text-[#2C3E50] border border-[#2C3E50]/15 hover:border-[#F39C12]/60 transition"
          >
            Back to Home
          </Link>
        </div>

        <p className="mt-6 text-center text-xs text-[#7F8C8D]">
          A confirmation will be sent to your mobile when your order ships.
        </p>
      </div>
    </div>
  );
};

export default OrderConfirmation;
